import { AxiosInstance } from 'axios';
import {
  CustomAssetBody,
  CustomAssetsDetails,
  CustomAssetsListParams,
  CustomAssetsListResponse,
  IncomeType,
  MarketCustomAssetBody,
} from './interfaces';
import {
  ICancelSubscriptionParams,
  IGetPaymentMethodResponse,
  IUpdatePaymentMethodParams,
  IWalletSimulationListResponse,
  IGetTeamThemeResponse,
  IProUserProfileResponse,
  IUpdateUserBody,
  ISimulationListParams,
} from './interfaces/userdata';
import { IGetTeamPortfolioResponse } from './interfaces/portfolio';

export interface CreateAsset {
  identifier?: string;
  displayName?: string | null;
  value: number;
  startDate: string | null; // ISO 8601 date string
  finalDate: string | null; // ISO 8601 date string
}

export interface SimulationPeriod {
  periodType: 'default' | 'custom' | 'assets';
  period?: 'overlap_period' | '6' | '12' | '24' | '36' | '48' | '60';
  periodStartDate?: string | null; // ISO 8601 date string
  periodEndDate?: string | null; // ISO 8601 date string
}

export interface CreateSimulation {
  name: string;
  assets: CreateAsset[];
  period: SimulationPeriod;
  simulationProfile?: string;
  isRecommended?: boolean;
  folderId?: string | null;
  tradingCurrency?: 'BRL' | 'USD' | 'EUR';
}

export interface ISimulationResponse {
  id: string;
  name: string;
  assets: {
    identifier: string;
    value: number;
    displayName: string | null | undefined;
    percent: number;
    nicename: string;
    startDate: string | null;
    finalDate: string | null;
  }[];
  period: SimulationPeriod;
  simulationProfile: 'conservative' | 'moderate' | 'aggressive';
  isRecommended: boolean;
  folderId: string | null;
  tradingCurrency: 'BRL' | 'USD' | 'EUR';
  createdAt: string;
  updatedAt: string;
  edit_by: 'value' | 'percent';
  config: any;
}

export class UserDataAPI {
  constructor(public readonly http: AxiosInstance) {}

  customAsset = {
    list: async (params?: CustomAssetsListParams) =>
      this._makeRequest<CustomAssetsListResponse>({
        endpoint: '/custom-assets',
        method: 'GET',
        params,
      }),
    get: async (id: string) =>
      this._makeRequest<CustomAssetsDetails>({
        endpoint: `/custom-assets/${id}`,
        method: 'GET',
      }),
    create: async (
      teamId: string,
      asset: CustomAssetBody | MarketCustomAssetBody
    ) =>
      this._makeRequest<{ id: string; identifier: string }>({
        endpoint: '/custom-assets',
        method: 'POST',
        body: { asset, team_id: teamId },
      }),
    update: async (
      id: string,
      asset: Partial<CustomAssetBody | MarketCustomAssetBody>
    ) =>
      this._makeRequest<{ id: string }>({
        endpoint: `/custom-assets/${id}`,
        method: 'PUT',
        body: { asset },
      }),
    delete: async (id: string) =>
      this._makeRequest<{ id: string }>({
        endpoint: `/custom-assets/${id}`,
        method: 'DELETE',
      }),
    updateQuotes: async (
      id: string,
      body: { quotes: { date: Date; value: number }[]; currency?: string }
    ) =>
      this._makeRequest<{ id: string }>({
        endpoint: `/custom-assets/${id}/quotes`,
        method: 'PUT',
        body,
      }),

    addIncome: async (
      id: string,
      body: {
        type: string;
        value: number;
        last_prior_date: string;
        payment_date: string;
      }
    ) => {
      return this._makeRequest({
        endpoint: `/custom-assets/${id}/incomes`,
        method: 'POST',
        body,
      });
    },
    updateIncomes: async ({
      id,
      body,
    }: {
      id: string;
      body: {
        incomes: {
          value: number;
          last_date_prior?: string;
          payment_date: string;
          type: IncomeType;
        }[];
      };
    }) =>
      this._makeRequest<void>({
        endpoint: `/custom-assets/${id}/incomes`,
        method: 'PUT',
        body,
      }),
    /**
     * Create new incomes, without removing the old ones
     * @param id - ID do ativo
     * @param body - Objeto com os dados das novas cotas
     * @param body.incomes - Array de objetos com os dados das novas cotas
     * @returns void
     */
    bulkCreateIncomes: async ({
      id,
      body,
    }: {
      id: string;
      body: {
        incomes: {
          value: number;
          last_date_prior: Date;
          payment_date: Date;
          type: IncomeType;
        }[];
      };
    }) =>
      this._makeRequest<void>({
        endpoint: `/custom-assets/${id}/incomes/batch`,
        method: 'POST',
        body,
      }),

    getCategories: async ({ team_id }: { team_id: string }) => {
      return this._makeRequest<any[]>({
        endpoint: `/custom-assets/category/${team_id}`,
        method: 'GET',
      });
    },
    createCategory: async ({
      body,
    }: {
      body: {
        team_id: string;
        name: string;
      };
    }) => {
      return this._makeRequest<any>({
        endpoint: `/custom-assets/category`,
        method: 'POST',
        body,
      });
    },
    deleteCategory: async ({ id }: { id: string }) => {
      return this._makeRequest<any>({
        endpoint: `/custom-assets/category/${id}`,
        method: 'DELETE',
      });
    },
  };

  public simulation = {
    list: (params?: ISimulationListParams) => {
      return this._makeRequest<IWalletSimulationListResponse>({
        endpoint: `/wallet-simulation`,
        method: 'GET',
        params,
      });
    },
    getWalletSimulation: (walletSimulationId: string) => {
      return this._makeRequest<ISimulationResponse>({
        endpoint: `/wallet-simulation/${walletSimulationId}`,
        method: 'GET',
      });
    },
    create: ({ body, teamId }: { body: CreateSimulation; teamId?: string }) => {
      return this._makeRequest<any>({
        endpoint: `wallet-simulation`,
        method: 'POST',
        body,
        params: { teamId },
      });
    },
    update: ({
      identifier,
      body,
    }: {
      identifier: string;
      body: CreateSimulation;
    }) => {
      return this._makeRequest<any>({
        endpoint: `wallet-simulation/${identifier}`,
        method: 'PUT',
        body,
      });
    },
    updateConfig: ({ identifier, body }: { identifier: string; body: any }) => {
      return this._makeRequest<any>({
        endpoint: `/pro/teams/wallet-simulation/${identifier}/config`,
        method: 'PUT',
        body,
      });
    },

    delete: ({ identifier }: { identifier: string }) => {
      return this._makeRequest<any>({
        endpoint: `wallet-simulation/${identifier}`,
        method: 'DELETE',
      });
    },

    copy: ({
      body,
    }: {
      body: {
        identifier?: string;
      };
    }) => {
      return this._makeRequest<any>({
        endpoint: `wallet-simulation/${body.identifier}/copy`,
        method: 'POST',
        body,
      });
    },

    appplyRecommend: ({
      identifier,
      isRecommended,
    }: {
      identifier: string;
      isRecommended: boolean;
    }) => {
      return this._makeRequest<any>({
        endpoint: `/wallet-simulation/${identifier}/recommend`,
        method: 'PATCH',
        body: {
          isRecommended,
        },
      });
    },

    moveToFolder: ({ teamId, body }: { teamId: string; body: any }) => {
      return this._makeRequest<any>({
        endpoint: `/pro/teams/${teamId}/folders/item`,
        method: 'POST',
        body,
      });
    },
  };

  public folder = {
    delete: ({ teamId, folderId }: { teamId: string; folderId: string }) => {
      return this._makeRequest<any>({
        endpoint: `/pro/teams/${teamId}/folders/${folderId}`,
        method: 'DELETE',
      });
    },
    create: ({
      teamId,
      name,
      type,
    }: {
      teamId: string;
      type?: string;
      name: string;
    }) => {
      return this._makeRequest<any>({
        endpoint: `/pro/teams/${teamId}/folders`,
        method: 'POST',
        body: { team_id: teamId, name, type },
      });
    },
    getFolder: ({ folderId, teamId }: { folderId: string; teamId: string }) => {
      return this._makeRequest<any>({
        endpoint: `/pro/teams/${teamId}/folders/${folderId}`,
        method: 'GET',
      });
    },

    moveToFolder: ({ teamId, body }: { teamId: string; body: any }) => {
      return this._makeRequest<any>({
        endpoint: `/pro/teams/${teamId}/folders/item`,
        method: 'PUT',
        body,
      });
    },

    deleteItem: ({ itemId, teamId }: { itemId: string; teamId: string }) => {
      return this._makeRequest<any>({
        endpoint: `/pro/teams/${teamId}/folders/item/${itemId}`,
        method: 'DELETE',
      });
    },
  };

  subscription = {
    getPaymentMethod: async () => {
      return this._makeRequest<IGetPaymentMethodResponse>({
        endpoint: `/subscriptions/payment-method`,
        method: 'GET',
      });
    },
    updatePaymentMethod: async ({
      subId,
      body,
    }: IUpdatePaymentMethodParams) => {
      return this._makeRequest<void>({
        endpoint: `/subscriptions/${subId}/payment-method`,
        method: 'PUT',
        body,
      });
    },
    cancel: async ({ subscriptionId, body }: ICancelSubscriptionParams) => {
      return this._makeRequest<void>({
        endpoint: `/subscriptions/${subscriptionId}`,
        method: 'DELETE',
        body,
      });
    },
  };

  public user = {
    getPROUserProfile: () => {
      return this._makeRequest<IProUserProfileResponse>({
        endpoint: `/pro/teams/user`,
        method: 'GET',
      });
    },
    resetPassword: () => {
      return this._makeRequest<void>({
        endpoint: `/users/reset-password`,
        method: 'POST',
      });
    },
    updateProfile: (body: IUpdateUserBody) => {
      return this._makeRequest<void>({
        endpoint: `/users`,
        method: 'PUT',
        body,
      });
    },
  };

  public team = {
    getTeamTheme: ({ teamId }: { teamId: string }) => {
      return this._makeRequest<IGetTeamThemeResponse>({
        endpoint: `/pro/teams/${teamId}/theme`,
        method: 'GET',
      });
    },
  };

  public portfolio = {
    listTeamPortfolios: ({ teamId }: { teamId: string }) =>
      this._makeRequest<IGetTeamPortfolioResponse>({
        endpoint: `/portfolio/${teamId}/portfolios`,
        method: 'GET',
      }),
  };

  private async _makeRequest<T>({
    endpoint,
    method,
    body = null,
    params = null,
    headers = null,
    safe = false,
    config = null,
  }: Params): Promise<T> {
    try {
      const { data } = await this.http({
        method: method,
        url: endpoint,
        ...(body && { data: body }),
        ...(params && { params }),
        ...(headers && { headers }),
        ...(config && { ...config }),
      });
      if (data === '') return null as T;

      return data;
    } catch (error: any) {
      const requestError = {
        url: error.config.url,
        method: error.config.method,
        status: error.response?.status,
        data: error.response?.data,
      };

      console.error('USERDATA API:', error?.request?._header, requestError);

      if (safe) return null as T;
      if (error.response?.data)
        throw {
          ...error.response?.data,
          data: error?.response?.data,
          status: error.response.status,
        };

      throw error;
    }
  }
}

interface Params {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  params?: any;
  headers?: any;
  safe?: boolean; // Se true, o método não irá lançar exceção caso não encontre cotação para o ativo, retornando null
  config?: any;
}
